import React, { useCallback, useState } from 'react';
import { Alert, Divider, Row, Col, Card } from 'antd';
import { MutationHookOptions, MutationTuple, QueryHookOptions, QueryResult } from '@apollo/client/react/types/types';
import styled from 'styled-components/macro';
import { useHistory } from 'react-router';
import { EntityType, Exact } from '../../../../../types.generated';
import { Notification } from '../../../../shared/Notification';
import { getEntityPath, sortEntityProfileTabs, useRoutedTab, useUpdateGlossaryEntityDataOnChange } from './utils';
import {
    EntitySidebarSection,
    EntitySubHeaderSection,
    EntityTab,
    GenericEntityProperties,
    GenericEntityUpdate,
} from '../../types';
import { EntityProfileNavBar } from './nav/EntityProfileNavBar';
import { EntityHeader } from './header/EntityHeader';
import { EntityTabs } from './header/EntityTabs';
import { EntitySidebar } from './sidebar/EntitySidebar';
import EntityContext from '../../EntityContext';
import useIsLineageMode from '../../../../lineage/utils/useIsLineageMode';
import { useEntityRegistry } from '../../../../useEntityRegistry';
import LineageExplorer from '../../../../lineage/LineageExplorer';
import CompactContext from '../../../../shared/CompactContext';
import DynamicTab from '../../tabs/Entity/weaklyTypedAspects/DynamicTab';
import analytics, { EventType } from '../../../../analytics';
import { EntityMenuItems } from '../../EntityDropdown/EntityDropdown';
import { useIsSeparateSiblingsMode } from '../../siblingUtils';
import { EntityActionItem } from '../../entity/EntityActions';
import { ErrorSection } from '../../../../shared/error/ErrorSection';
import { EntityHead } from '../../../../shared/EntityHead';
import useGetDataForProfile from './useGetDataForProfile';
import NonExistentEntityPage from '../../entity/NonExistentEntityPage';
import useIsChangeLogMode from './utils/useIsChangeLogMode';
import ChangeLogs from './ChangeLogs';
import { useRbac } from '../../useRbac';
import { useAppConfig } from '../../../../useAppConfig';
import { useUpdateDomainEntityDataOnChange } from '../../../../domain/utils';
import { useGetGlossaryTerms } from '../../tabs/Glossary/useGetGlossaryTerms';
import { useExportEntityMutation } from '../../../../../graphql/mutations.generated';
import { ComponentSpinner } from '../../../../shared/ComponentSpinner';

export type ExportObjectGlossaryType = {
    glossaryTerm: {
        glossaryNodeUrns: string[];
        glossaryTermUrns: string[];
    };
    entityType: string;
};

type Props<T, U> = {
    urn: string;
    entityType: EntityType;
    useEntityQuery: (
        baseOptions: QueryHookOptions<
            T,
            Exact<{
                urn: string;
            }>
        >,
    ) => QueryResult<
        T,
        Exact<{
            urn: string;
        }>
    >;
    useUpdateQuery?: (
        baseOptions?: MutationHookOptions<U, { urn: string; input: GenericEntityUpdate }> | undefined,
    ) => MutationTuple<U, { urn: string; input: GenericEntityUpdate }>;
    getOverrideProperties: (T) => GenericEntityProperties;
    tabs: EntityTab[];
    sidebarSections: EntitySidebarSection[];
    subHeader?: EntitySubHeaderSection;
    headerDropdownItems?: Set<EntityMenuItems>;
    headerActionItems?: Set<EntityActionItem>;
    hideBrowseBar?: boolean;
    isNameEditable?: boolean;
    isExportable?: boolean;
    exportObj?: ExportObjectGlossaryType;
};

const MAX_COMPACT_WIDTH = 490 - 24 * 2;

const ContentContainer = styled.div`
    height: auto;
    min-height: 100%;
    flex: 1;
    min-width: 0;
`;

const HeaderAndTabs = styled.div`
    flex-grow: 1;
`;

const HeaderAndTabsFlex = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    max-height: 100%;
    overflow: hidden;
    min-height: 0;
    overflow-y: auto;

    &::-webkit-scrollbar {
        height: 12px;
        width: 2px;
        background: #f2f2f2;
    }
    &::-webkit-scrollbar-thumb {
        background: #cccccc;
        -webkit-border-radius: 1ex;
        -webkit-box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.75);
    }
`;
// const Sidebar = styled.div<{ $width: number }>`
//     max-height: 100%;
//     overflow: auto;
//     flex-shrink: 1;
//     padding-left: 8px;
//     width: 300px;
// `;

const Header = styled.div`
    flex-shrink: 0;
`;

const TabContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: auto;
`;

const CompactProfile = styled.div`
    max-width: ${MAX_COMPACT_WIDTH};
`;

const defaultTabDisplayConfig = {
    visible: (_, _1) => true,
    enabled: (_, _1) => true,
};

const defaultSidebarSection = {
    visible: (_, _1) => true,
};

/**
 * Container for display of the Entity Page
 */
export const EntityProfile = <T, U>({
    urn,
    useEntityQuery,
    useUpdateQuery,
    entityType,
    getOverrideProperties,
    tabs,
    sidebarSections,
    headerDropdownItems,
    headerActionItems,
    isNameEditable,
    hideBrowseBar,
    subHeader,
    isExportable,
    exportObj,
}: Props<T, U>): JSX.Element => {
    const isChangeLogMode = useIsChangeLogMode();
    const { canViewChangeLog } = useRbac();
    const canViewAuditLog = canViewChangeLog(entityType);
    const isLineageMode = useIsLineageMode();
    const isHideSiblingMode = useIsSeparateSiblingsMode();
    const entityRegistry = useEntityRegistry();
    const history = useHistory();
    const appConfig = useAppConfig();
    const [exportEntityMutation] = useExportEntityMutation();
    const isCompact = React.useContext(CompactContext);
    const tabsWithDefaults = tabs.map((tab) => ({ ...tab, display: { ...defaultTabDisplayConfig, ...tab.display } }));
    const sortedTabs = sortEntityProfileTabs(appConfig.config, entityType, tabsWithDefaults);
    const sideBarSectionsWithDefaults = sidebarSections.map((sidebarSection) => ({
        ...sidebarSection,
        display: { ...defaultSidebarSection, ...sidebarSection.display },
    }));

    const [exportEntity, setExportEntity]: any = useState(null);
    const [isCsvExporting, setIsCsvExporting] = useState<boolean>(false);

    const [shouldRefetchEmbeddedListSearch, setShouldRefetchEmbeddedListSearch] = useState(false);

    const handleExport = (optionsObj: ExportObjectGlossaryType) => {
        setIsCsvExporting(true);
        setExportEntity(null);
        exportEntityMutation({
            variables: {
                input: optionsObj,
            },
        })
            .then((res = {}) => {
                setExportEntity(res.data?.exportEntity);
            })
            .catch((e) => {
                if (e.networkError.statusCode === 503) {
                    const updatedInput = {
                        ...optionsObj,
                        async: true,
                    };
                    // Retry with a different input for 504 errors
                    exportEntityMutation({
                        variables: {
                            input: updatedInput,
                        },
                    }).then((retryRes) => {
                        const { data } = retryRes;
                        setExportEntity(data?.exportEntity);
                    });
                }
            })
            .finally(() => setIsCsvExporting(false));
    };

    const routeToTab = useCallback(
        ({
            tabName,
            tabParams,
            method = 'replace',
        }: {
            tabName: string;
            tabParams?: Record<string, any>;
            method?: 'push' | 'replace';
        }) => {
            analytics.event({
                type: EventType.EntitySectionViewEvent,
                entityType,
                entityUrn: urn,
                section: tabName.toLowerCase(),
            });
            history[method](
                getEntityPath(entityType, urn, entityRegistry, false, isHideSiblingMode, tabName, tabParams),
            );
        },
        [history, entityType, urn, entityRegistry, isHideSiblingMode],
    );

    const { entityData, dataPossiblyCombinedWithSiblings, dataNotCombinedWithSiblings, loading, error, refetch } =
        useGetDataForProfile({ urn, entityType, useEntityQuery, getOverrideProperties });

    useUpdateGlossaryEntityDataOnChange(entityData, entityType);
    useUpdateDomainEntityDataOnChange(entityData, entityType);

    const maybeUpdateEntity = useUpdateQuery?.({
        onCompleted: () => refetch(),
    });
    let updateEntity;
    if (maybeUpdateEntity) {
        [updateEntity] = maybeUpdateEntity;
    }

    const lineage = entityData ? entityRegistry.getLineageVizConfig(entityType, entityData) : undefined;

    const autoRenderTabs: EntityTab[] =
        entityData?.autoRenderAspects?.map((aspect) => ({
            name: aspect.renderSpec?.displayName || aspect.aspectName,
            component: () => (
                <DynamicTab
                    renderSpec={aspect.renderSpec}
                    type={aspect.renderSpec?.displayType}
                    payload={aspect.payload}
                />
            ),
            display: {
                visible: () => true,
                enabled: () => true,
            },
        })) || [];

    const visibleTabs = [...sortedTabs, ...autoRenderTabs].filter((tab) =>
        tab.display?.visible(entityData, dataPossiblyCombinedWithSiblings),
    );

    const enabledAndVisibleTabs = visibleTabs.filter((tab) =>
        tab.display?.enabled(entityData, dataPossiblyCombinedWithSiblings),
    );

    const routedTab = useRoutedTab(enabledAndVisibleTabs);

    const { glossaryTermsData, refetchGlossaryTerms, isGlossaryTermsLoading } = useGetGlossaryTerms(urn);

    if (entityData?.exists === false) {
        return <NonExistentEntityPage />;
    }

    if (isCompact) {
        return (
            <EntityContext.Provider
                value={{
                    urn,
                    entityType,
                    entityData,
                    loading,
                    baseEntity: dataPossiblyCombinedWithSiblings,
                    dataNotCombinedWithSiblings,
                    updateEntity,
                    routeToTab,
                    refetch,
                    lineage,
                    shouldRefetchEmbeddedListSearch,
                    setShouldRefetchEmbeddedListSearch,
                    glossaryTermsData,
                    refetchGlossaryTerms,
                    isGlossaryTermsLoading,
                }}
            >
                <>
                    {loading && <ComponentSpinner />}
                    {error && !entityData && <ErrorSection />}
                    {!loading && (
                        <CompactProfile>
                            <EntityHeader
                                headerDropdownItems={headerDropdownItems}
                                headerActionItems={headerActionItems}
                                subHeader={subHeader}
                            />
                            <Divider style={{ marginBottom: '0' }} />
                            <EntitySidebar sidebarSections={sideBarSectionsWithDefaults} />
                        </CompactProfile>
                    )}
                </>
            </EntityContext.Provider>
        );
    }

    const isBrowsable = entityRegistry.getBrowseEntityTypes().includes(entityType);
    const isLineageEnabled = entityRegistry.getLineageEntityTypes().includes(entityType);
    // Entities that are neither browsable nor lineage-enabled (e.g. Domain) still need the nav bar
    // rendered so users can reach the Change Log view, provided they are allowed to view it.
    const showBrowseBar = (isBrowsable || isLineageEnabled || canViewAuditLog) && !hideBrowseBar;

    const Component = () => {
        if (isLineageMode) {
            return <LineageExplorer type={entityType} urn={urn} />;
        }
        if (isChangeLogMode && canViewAuditLog) {
            return <ChangeLogs urn={urn} />;
        }

        return (
            <>
                {isExportable && exportEntity && <Notification exportEntity={exportEntity} />}

                <Row wrap={false} className="f-layout-wrap-xs fill-height">
                    <Col xs={24} lg={18}>
                        <HeaderAndTabs>
                            <HeaderAndTabsFlex>
                                {isExportable && isCsvExporting && <ComponentSpinner />}
                                <Header>
                                    <Card bordered={false} className="mb-1">
                                        <EntityHeader
                                            headerDropdownItems={headerDropdownItems}
                                            headerActionItems={headerActionItems}
                                            isNameEditable={isNameEditable}
                                            subHeader={subHeader}
                                            isExportable={isExportable}
                                            onExportClicked={() => handleExport(exportObj as ExportObjectGlossaryType)}
                                        />
                                    </Card>
                                    <EntityTabs tabs={visibleTabs} selectedTab={routedTab} />
                                </Header>
                                <TabContent>
                                    {routedTab && <routedTab.component properties={routedTab.properties} />}
                                </TabContent>
                            </HeaderAndTabsFlex>
                        </HeaderAndTabs>
                    </Col>
                    <Col flex="auto" className="f-mt-2-xs f-border-left" style={{ overflow: 'hidden' }}>
                        <EntitySidebar sidebarSections={sideBarSectionsWithDefaults} />
                    </Col>
                </Row>
            </>
        );
    };

    return (
        <>
            {loading && <ComponentSpinner />}
            <EntityContext.Provider
                value={{
                    urn,
                    entityType,
                    entityData,
                    loading,
                    baseEntity: dataPossiblyCombinedWithSiblings,
                    dataNotCombinedWithSiblings,
                    updateEntity,
                    routeToTab,
                    refetch,
                    lineage,
                    shouldRefetchEmbeddedListSearch,
                    setShouldRefetchEmbeddedListSearch,
                    glossaryTermsData,
                    refetchGlossaryTerms,
                    isGlossaryTermsLoading,
                }}
            >
                <>
                    <EntityHead />
                    {showBrowseBar && <EntityProfileNavBar urn={urn} entityType={entityType} />}
                    {entityData?.status?.removed === true && (
                        <Alert
                            message="This entity is not discoverable via search or lineage graph. Contact your admin for more information."
                            banner
                        />
                    )}
                    {error && !urn && <ErrorSection />}
                    {!loading && <ContentContainer>{Component()}</ContentContainer>}
                </>
            </EntityContext.Provider>
        </>
    );
};
